<template>
  <div class="apps-gallery">
    <div v-for="category in filteredCategories" class="apps-gallery__container" :key="category.key">
      <span class="apps-gallery__category">{{ displayCategory(category.key) }}</span>

      <div class="apps-gallery__list">
        <mini-app-card
          v-for="service in category.items"
          @increment="$emit('increment', service)"
          @decrement="$emit('decrement', service)"
          :key="service.type"
          :app="service"
          :insertion-scope="appsInserted"
          :disabled="disabled"
          :dependency="getDependencies(service.type)"
        />
      </div>
    </div>

    <div v-if="filteredCollectionIsEmpty" class="apps-gallery__empty">
      <span>{{ $t("WorkspaceEdit.addApps.labels.empty") }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { CategoryItem, InsertedAppRegistry } from "@/views/workspaceEdit/lib/types"
import { defineComponent, computed, PropType } from "@vue/composition-api"
import { Dictionary } from "vue-router/types/router"
import { Filter } from "./utils/types"
import { ServiceItem } from "@/types/workspace"
import { useStore } from "@/composable/useStore"
import MiniAppCard from "./MiniAppCard.vue"

export default defineComponent({
  name: "ServiceGallery",
  props: {
    filter: {
      type: Object as PropType<Filter>,
      required: true
    },
    appsInserted: {
      type: Object as PropType<Dictionary<InsertedAppRegistry>>,
      required: true
    },
    appCategoryItemMap: {
      type: Object as PropType<Dictionary<CategoryItem<ServiceItem>>>,
      required: true
    }
  },
  setup(props) {
    const { spaceModule, organizationModule } = useStore()

    const disabled = computed(() => {
      return !organizationModule.mediaConnectIsSupported
    })

    const filteredCategories = computed(() => {
      const appsInCategories = props.appCategoryItemMap

      if (props.filter.tags.length > 0) {
        return _filterTags()
      }

      if (props.filter.search.length > 0) {
        return _filterSnippet()
      }

      return appsInCategories
    })

    const filteredCollectionIsEmpty = computed(() => {
      return Object.values(filteredCategories.value).length === 0
    })

    const displayCategory = (categoryKey: string) => {
      return categoryKey.replace(/[-]/g, " ")
    }

    const getDependencies = (serviceType: string) => {
      const models = spaceModule.serviceModels
      const service = models.find(model => model.type === serviceType)

      if (service) {
        return service.dependsOn
      }

      return []
    }

    const _filterTags = () => {
      const tags = props.filter.tags
      const filter = {} as Dictionary<CategoryItem<ServiceItem>>
      const appsInCategories = props.appCategoryItemMap

      if (tags.length) {
        for (const key in appsInCategories) {
          const category = appsInCategories[key]

          if (category && tags.includes(category.key)) {
            filter[key] = category
          }
        }
      }

      return filter
    }

    const _filterSnippet = () => {
      const filter = {} as Dictionary<CategoryItem<ServiceItem>>
      const snippet = props.filter.search
      const appsInCategories = props.appCategoryItemMap

      for (const key in appsInCategories) {
        const category = appsInCategories[key]

        if (category) {
          const matchesText = category.items.some(app => _compareSnippet(app.text, snippet))

          if (matchesText) {
            filter[key] = category
          }
        }
      }

      return filter
    }

    const _compareSnippet = (textOne: string, textTwo: string) => {
      return textOne.toLowerCase().includes(textTwo.toLowerCase())
    }

    return {
      getDependencies,
      disabled,
      filteredCollectionIsEmpty,
      filteredCategories,
      displayCategory
    }
  },
  components: {
    MiniAppCard
  }
})
</script>
